import {useTranslation} from 'react-i18next';

import {WorkdayCollapsibleTile} from '../WorkdayCollapsibleTile';
import {OfficeWorkday} from '@lib/store/src/data/timeslots/apiTypes';
import {
  getAllConnectedUsers,
  getGroupWithWorkdayCountByDate,
  getGroupWithWorkdayCountForDateAndBuilding,
  useAppSelector,
} from '@lib/store';
import {useConnectionsSchedule} from '@lib/store/src/ui/hooks/timeslots/useConnectionsSchedule';
import {constructConnectionsOverview} from '@lib/store/src/data/timeslots/viewmodel-helpers/constructConnectionsOverview';
import {AvatarStack} from '@molecules';
import {ConnectionOverview} from 'src/components/organisms/home';
import {ModalPage} from 'src/components/organisms/ModalPage';
import {useModal} from '@hooks';
import {dateToLocalDateString} from '@lib/store/src/data/timeslots/dateTimeHelpers';

interface WorkdayConnectionsSectionProps {
  workday: OfficeWorkday | undefined;
  date: Date;
  handleClassName?: string;
}

const limit = 5;

export const WorkdayConnectionsSection = ({date, workday, handleClassName}: WorkdayConnectionsSectionProps) => {
  const {t} = useTranslation();
  const {addPages, openModal, closeModal} = useModal();
  const dateKey = dateToLocalDateString(date);

  const referenceLocation = workday ? {buildingId: workday.buildingId} : undefined;

  const connectionsSchedule = useConnectionsSchedule(dateKey);
  const connectedUsers = useAppSelector(getAllConnectedUsers);

  const groupsWithWorkdayCount = useAppSelector((state) => getGroupWithWorkdayCountByDate(state, dateKey));
  const groupsWithWorkdayCountForLocation = useAppSelector((state) =>
    getGroupWithWorkdayCountForDateAndBuilding(state, dateKey, referenceLocation?.buildingId || ''),
  );

  const filteredGroupWithWorkdayCount = referenceLocation ? groupsWithWorkdayCountForLocation : groupsWithWorkdayCount;

  const overview = constructConnectionsOverview({
    connectedUsers,
    // If user doesn't have a workday set, show all connections with office workdays
    // Otherwise only show connections for the given workday building
    connectionWorkdays: workday === undefined ? connectionsSchedule.workdays : undefined,
    connectionWorkspaceReservations: connectionsSchedule.workspaceResevations,
    referenceLocation,
  });

  const connections = overview.flatMap((section) => section.occupants.map((occupant) => occupant.user));
  const isEmpty = connections.length === 0 && filteredGroupWithWorkdayCount.length === 0;

  return (
    <WorkdayCollapsibleTile
      onPress={() => {
        addPages([
          <ModalPage
            title={t('connection:AtTheOffice')}
            onClose={closeModal}>
            <div className="py-4">
              <ConnectionOverview
                dateKey={dateKey}
                hideTitle
              />
            </div>
          </ModalPage>,
        ]);

        openModal();
      }}
      handle={
        !isEmpty ? (
          <AvatarStack
            date={dateKey}
            overlap
            users={connections}
            groupsWithCounts={filteredGroupWithWorkdayCount}
            max={limit}
          />
        ) : (
          <p className="text-[15px]">{t('connection:NoConnectionsHereShort')}</p>
        )
      }
      handleClassName={handleClassName}
      icon="user"
    />
  );
};
